interface LaunchViewProps {
  readonly profileName: string;
  readonly hasPortfolio: boolean;
  readonly onAddHoldings: () => void;
  readonly onCreatePortfolio: () => void;
  readonly onStateWhatIf: () => void;
  readonly onReplayCase: () => void;
  readonly onWatchNumber: () => void;
  readonly onOpenDashboard: () => void;
}

const PRIMARY_CARDS = [
  {
    key: "holdings",
    kicker: "Start here",
    title: "Add what you own",
    body: "Search for stocks and funds, then enter your holdings. You can also import a spreadsheet. Personal investors can start with names only.",
    consequence: "Review your holdings before saving",
    cta: "Add holdings →",
  },
  {
    key: "portfolio",
    kicker: "Organize",
    title: "Create a portfolio",
    body: "Name a group of holdings you want watched together — a retirement account, a taxable account, one strategy. You can hold several.",
    consequence: "Starts as a draft · turn on monitoring when you are ready",
    cta: "Create a portfolio →",
  },
  {
    key: "what-if",
    kicker: "Ask",
    title: "Explore a what-if",
    body: "Say what you are worried about in your own words. Polyfuture works out how it would reach what you own, and what evidence would confirm or contradict it.",
    consequence: "Needs at least one holding to be useful",
    cta: "Explore a what-if →",
  },
] as const;

export function LaunchView({
  profileName,
  hasPortfolio,
  onAddHoldings,
  onCreatePortfolio,
  onStateWhatIf,
  onReplayCase,
  onWatchNumber,
  onOpenDashboard,
}: LaunchViewProps) {
  const primaryActions = {
    holdings: onAddHoldings,
    portfolio: onCreatePortfolio,
    "what-if": onStateWhatIf,
  } as const;

  return (
    <section className="launch-screen">
      <header>
        <p className="eyebrow">Set up · {profileName}</p>
        <h1>What do you want to do first?</h1>
        <p>
          Start with your holdings or a question about your investments. Monitoring is off until you
          set it up and turn it on.
        </p>
      </header>

      <div className="launch-primary-grid">
        {PRIMARY_CARDS.map((card, index) => (
          <article
            className={index === 0 ? "launch-card launch-card-primary" : "launch-card"}
            key={card.key}
          >
            <p className="eyebrow">{card.kicker}</p>
            <h2>{card.title}</h2>
            <p>{card.body}</p>
            <small>{card.consequence}</small>
            <button
              className={index === 0 ? "button button-primary" : "button button-secondary"}
              type="button"
              onClick={primaryActions[card.key]}
            >
              {card.cta}
            </button>
          </article>
        ))}
      </div>

      {!hasPortfolio ? (
        <p className="launch-prerequisite" role="status">
          We will help you create a portfolio before adding holdings or running a what-if.
        </p>
      ) : null}

      <details className="advanced-options">
        <summary>More ways to explore</summary>
        <div className="launch-secondary-grid">
          <button type="button" onClick={onReplayCase}>
            <strong>Replay a worked case</strong>
            <span>
              A reconstructed case, step by step, with the numbers you can change yourself.
            </span>
          </button>
          <button type="button" onClick={onWatchNumber}>
            <strong>Watch a number for me</strong>
            <span>
              Choose a measure and an alert level, then connect a source and schedule the checks.
            </span>
          </button>
          <button type="button" onClick={onOpenDashboard}>
            <strong>Skip to Dashboard</strong>
            <span>See your portfolios, analysis progress, and monitoring status.</span>
          </button>
        </div>
      </details>
    </section>
  );
}
